// localStorage-backed overlay mapping encounterId → reviewerId for the
// assign-to-reviewer demo mock. Pure client state — never written to RxDB,
// never replicated to the backend.
import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "epocus.demoAssignments";
// Same-tab change notifier — the native "storage" event only fires across tabs.
const SAME_TAB_EVENT = "epocus.demoAssignments.change";

type Assignments = Record<string, string>;

const read = (): Assignments => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Assignments;
    }
    return {};
  } catch {
    return {};
  }
};

const write = (next: Assignments): void => {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent(SAME_TAB_EVENT));
};

export const getAssignments = read;

export const assignReviewer = (
  encounterId: string,
  reviewerId: string,
): void => {
  const next = { ...read(), [encounterId]: reviewerId };
  write(next);
};

export const clearAssignment = (encounterId: string): void => {
  const current = read();
  if (!(encounterId in current)) return;
  const next = { ...current };
  delete next[encounterId];
  write(next);
};

const subscribe = (listener: () => void): (() => void) => {
  const handler = (e: Event) => {
    if (e instanceof StorageEvent && e.key !== STORAGE_KEY) return;
    listener();
  };
  window.addEventListener("storage", handler);
  window.addEventListener(SAME_TAB_EVENT, handler);
  return () => {
    window.removeEventListener("storage", handler);
    window.removeEventListener(SAME_TAB_EVENT, handler);
  };
};

// Stable empty reference so SSR / no-window environments don't break useSyncExternalStore.
const EMPTY: Assignments = {};
const getServerSnapshot = (): Assignments => EMPTY;

// Cached snapshot — useSyncExternalStore requires the same reference between
// renders when the underlying data hasn't changed. Re-reading localStorage and
// returning a new object on every call causes an infinite render loop.
let snapshotCache: Assignments = EMPTY;
let snapshotCacheRaw: string | null = null;

const getSnapshot = (): Assignments => {
  if (typeof window === "undefined") return EMPTY;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === snapshotCacheRaw) return snapshotCache;
  snapshotCacheRaw = raw;
  snapshotCache = read();
  return snapshotCache;
};

export const useDemoAssignments = (): Assignments =>
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

export const useDemoAssignmentFor = (
  encounterId: string | null | undefined,
): string | null => {
  const all = useDemoAssignments();
  return encounterId ? (all[encounterId] ?? null) : null;
};

// Re-exported as a hook so callers don't need to import the setters directly.
export const useAssignReviewer = (): ((
  encounterId: string,
  reviewerId: string,
) => void) => useCallback(assignReviewer, []);
